/**
 * toolLandings
 * ---------------------------------------------------------------------------
 * Single source of truth for the public, separately-marketable entry pages:
 *   psyforge.com/astrology, /career, /relationships, /enterprise, /liker
 *
 * Each entry drives:
 *  - the guest landing page (ToolLandingPage)
 *  - the "Start where you're curious" grid on the visitor home page
 *  - the sitemap entries (scripts/generate-sitemap.mjs reads the slugs below)
 *
 * Copy is written for ONE audience per page. Do not cross-pollinate: the
 * whole point is that an astrology visitor never has to read enterprise copy.
 */

export type ToolSlug = 'astrology' | 'career' | 'relationships' | 'enterprise' | 'liker';

export interface ToolLandingStep {
  title: string;
  body: string;
  time?: string;
}

export interface ToolLandingFaq {
  q: string;
  a: string;
}

export interface ToolAudienceFit {
  /** Three lines describing who this page is for. */
  for: string[];
  /** Two honest lines about when this is the wrong tool. */
  notFor: string[];
}

export interface ToolBuiltOn {
  /** Framework / method labels behind the tool. */
  items: string[];
  /** Optional one-line explanation of the method. */
  note?: string;
}

export interface ToolVoice {
  /** An illustrative line of the kind of thing the tool tells you. */
  quote: string;
  /** What produced it, e.g. "Role match reasoning". */
  source: string;
}

export interface ToolLanding {
  slug: ToolSlug;
  /** Canonical public path — also the member-facing tool route. */
  path: string;
  /** Short label used in grids and cross-sell tiles. */
  label: string;
  /** Audience this page is marketed to (used in copy + analytics intent). */
  audience: string;
  headline: string;
  /** Optional phrase inside `headline` rendered in the tool's accent colour. */
  headlineAccent?: string;
  promise: string;
  /** Time cost shown honestly on cards and steps. */
  timeCost: string;
  /** 3-4 concrete outputs of the tool. */
  outputs: { title: string; body: string }[];
  steps: ToolLandingStep[];
  faqs: ToolLandingFaq[];
  /** Optional: who the tool suits, and when it does not. */
  audienceFit?: ToolAudienceFit;
  /** Optional: the frameworks / methods behind it. */
  builtOn?: ToolBuiltOn;
  /** Optional: illustrative examples of what the tool says. */
  voices?: ToolVoice[];
  /** Where a new account lands first — the assessment that unlocks the tool. */
  onboardingPath: string;
  /** Nudge shown to members once they have a profile. */
  crossSell: { slug: ToolSlug | 'personality'; label: string; reason: string; path: string };
  seo: { title: string; description: string };
  /** Accent token used for the page's gradient wash. */
  accent: 'primary' | 'secondary' | 'accent' | 'success' | 'info';
}


export const TOOL_LANDINGS: Record<ToolSlug, ToolLanding> = {
  astrology: {
    slug: 'astrology',
    path: '/astrology',
    label: 'Astrology',
    audience: 'people who want a real birth chart, read properly',
    headline: 'Your birth chart, read with actual precision',
    headlineAccent: 'actual precision',
    promise:
      'A calculated natal chart — Placidus or Whole Sign — with plain-language readings of every placement, plus synastry with the people who matter.',
    timeCost: 'Chart in 2 minutes from your birth details',
    outputs: [
      { title: 'A calculated natal chart', body: 'Planets, houses, aspects and angles from your exact birth time and place — not a sun-sign generator.' },
      { title: 'Placement-by-placement readings', body: 'Each planet in sign and house explained in language you can use, written for your chart rather than pasted from a template.' },
      { title: 'Synastry with someone else', body: 'Compare two charts side by side and see where the friction and the ease actually come from.' },
      { title: 'A downloadable chart report', body: 'Your full chart and readings as a clean PDF you can keep or share.' },
    ],
    steps: [
      { title: 'Enter your birth details', body: 'Date, exact time and place. We calculate the chart from ephemeris data, not lookup tables.', time: '2 min' },
      { title: 'Read your placements', body: 'Work through your chart one placement at a time, or read the whole interpretation top to bottom.' },
      { title: 'Add a second chart', body: 'Invite a partner or enter their details to see the synastry between you.' },
    ],
    faqs: [
      { q: 'Which house system do you use?', a: 'Both. Placidus is the default, and you can switch the whole chart to Whole Sign at any time — the readings follow the system you pick.' },
      { q: 'Do I need my exact birth time?', a: 'For houses and the ascendant, yes — a few minutes changes them. Without a time you still get accurate planetary signs and aspects.' },
      { q: 'Does astrology affect my personality results?', a: 'No. It is a separate, private layer. It never feeds your psychological profile or how you are matched with anyone.' },
    ],
    audienceFit: {
      for: [
        'You know your birth date, time and place and want the chart calculated properly.',
        'You already read charts and are tired of sun-sign summaries.',
        'You want to compare your chart with a partner, friend or family member.',
      ],
      notFor: [
        'You want predictions about specific events or dates.',
        'You want astrology to decide something for you rather than describe a pattern.',
      ],
    },
    builtOn: {
      items: ['Ephemeris positions', 'Placidus houses', 'Whole Sign houses', 'Aspect geometry', 'Synastry overlays'],
      note: 'Planetary positions are solved from orbital elements, not looked up from a table.',
    },
    voices: [
      { quote: 'Moon in the 12th: your first reaction to feeling is to hide it, then explain it later.', source: 'Placement reading' },
      { quote: 'His Saturn sits on your Venus — the steadying you asked for is the same thing that feels like a brake.', source: 'Synastry' },
      { quote: 'Switching to Whole Sign moves your Mercury into the 10th. Both readings are shown.', source: 'House system note' },
    ],
    onboardingPath: '/astrology',

    crossSell: { slug: 'personality', label: 'Your personality profile', reason: 'See how your chart lines up with a measured profile.', path: '/tps-onboarding' },
    seo: {
      title: 'Free Birth Chart & Astrology Readings | Psyforge',
      description: 'Calculate a precise natal chart with Placidus or Whole Sign houses, read every placement in plain language, and compare charts with synastry.',
    },
    accent: 'accent',
  },

  career: {
    slug: 'career',
    path: '/career',
    label: 'Career Finder',
    audience: 'people deciding what work to do next',
    headline: 'Find the work that actually fits you',
    headlineAccent: 'actually fits you',
    promise:
      'A measured profile of how you think, decide and work — turned into concrete role matches, a tailored resume and a coach who knows your patterns.',
    timeCost: '15-minute assessment, results the same session',
    outputs: [
      { title: 'Ranked role matches', body: 'Roles scored against your interests, decision style and working preferences — with the reason each one fits or does not.' },
      { title: 'A resume tailored per job', body: 'Paste a job post and get your own experience rewritten to answer what that employer is actually screening for.' },
      { title: 'A career plan you can act on', body: 'Next moves, skill gaps and a realistic sequence rather than a list of job titles.' },
      { title: 'A career coach that remembers', body: 'An AI coach carrying your profile and your history, so you are not re-explaining yourself every session.' },
    ],
    steps: [
      { title: 'Take the assessment', body: 'One 15-minute assessment produces your working profile — interests, decision style, and how you handle pressure and people.', time: '15 min' },
      { title: 'See your matches', body: 'Roles and fields ranked for you, each with the evidence behind the score.' },
      { title: 'Work the plan', body: 'Tailor applications, track offers, and talk it through with the coach.' },
    ],
    faqs: [
      { q: 'Is this just a job-title quiz?', a: 'No. Matches are built from a measured profile across established frameworks — interests, personality and work style — and each score shows the traits that produced it.' },
      { q: 'Will it write my resume for me?', a: 'It rewrites your own experience against a specific job post. You supply the history; it handles emphasis, ordering and language.' },
      { q: 'What does it cost to start?', a: 'The assessment and your role matches are free with an account. Deeper reports and coaching sessions are part of the paid tier.' },
    ],
    audienceFit: {
      for: [
        'You are choosing between fields, or wondering whether to leave the one you are in.',
        'You are applying now and keep rewriting the same resume for every post.',
        'You want the reason behind a suggested role, not a job-title horoscope.',
      ],
      notFor: [
        'You need a job board — this explains fit, it does not list vacancies.',
        'You want a hiring or screening tool for other people.',
      ],
    },
    builtOn: {
      items: ['Holland codes (RIASEC)', 'Big Five', 'MBTI cognitive functions', 'DiSC work styles', 'Enneagram motivation'],
      note: 'Every role score shows which measured traits produced it.',
    },
    voices: [
      { quote: 'Product manager, 91%: you decide with incomplete information and read a room fast.', source: 'Role match reasoning' },
      { quote: 'This post screens for stakeholder management. Your third bullet is the strongest evidence — it now leads.', source: 'Resume tailoring' },
      { quote: 'You have turned down two roles for the same reason. That is a boundary, not indecision.', source: 'Career coach' },
    ],
    onboardingPath: '/tps-onboarding?intent=career',

    crossSell: { slug: 'relationships', label: 'Relationship report', reason: 'The same profile explains how you work with people, not just what work suits you.', path: '/relationships' },
    seo: {
      title: 'Career Personality Test & Career Finder | Psyforge',
      description: 'Find careers that fit how you actually think and work. Ranked role matches, resume tailoring for each job post, and an AI career coach built on your profile.',
    },
    accent: 'info',
  },

  relationships: {
    slug: 'relationships',
    path: '/relationships',
    label: 'Relationship Report',
    audience: 'couples and friends who want to understand their dynamic',
    headline: 'Understand the dynamic between you',
    headlineAccent: 'the dynamic',
    promise:
      'Both of you take the assessment; you get one honest report on where you fit, where you grind, and what to actually do about it.',
    timeCost: '15 minutes each, report as soon as you both finish',
    outputs: [
      { title: 'A compatibility report for the two of you', body: 'Written about your specific pairing — not a chart of which types get along.' },
      { title: 'Where friction comes from', body: 'The trait differences that cause your recurring arguments, named plainly, with what each of you is actually doing.' },
      { title: 'What works between you', body: 'The genuine strengths of the pairing, so the report is usable rather than a list of problems.' },
      { title: 'Concrete things to try', body: 'Specific adjustments for each of you, matched to your two profiles.' },
    ],
    steps: [
      { title: 'Take the assessment', body: 'Fifteen minutes builds your profile across several established frameworks.', time: '15 min' },
      { title: 'Invite your person', body: 'Send one link. They answer for themselves — you never see their raw answers, only the shared report.' },
      { title: 'Read it together', body: 'Go through the report side by side, then work the suggestions.' },
    ],
    faqs: [
      { q: 'Does my partner need an account?', a: 'They answer through an invite link. Creating an account lets them keep their own profile, but the report works either way.' },
      { q: 'Can they see my answers?', a: 'No. Individual answers stay private; the report only shows the shared dynamic.' },
      { q: 'Does it work for friends and family?', a: 'Yes. The report is about the dynamic between two people — romance is not assumed anywhere in it.' },
    ],
    audienceFit: {
      for: [
        'You keep having the same argument and cannot name what starts it.',
        'You are both willing to answer honestly and read the result together.',
        'You want the strengths of the pairing named as clearly as the friction.',
      ],
      notFor: [
        'Only one of you is interested — the report needs both profiles.',
        'You want a verdict on whether to stay or leave. It describes the dynamic; the decision is yours.',
      ],
    },
    builtOn: {
      items: ['Big Five differentials', 'Attachment style', 'MBTI function pairing', 'Enneagram motivation', 'Love languages'],
      note: 'Fit comes from trait-by-trait comparison of two profiles, not a type-compatibility chart.',
    },
    voices: [
      { quote: 'You want the repair immediately; they need to go quiet first. The timing is the argument.', source: 'Friction analysis' },
      { quote: 'Joint decisions hold up better than either of you alone — you bring momentum, they bring the check.', source: 'Strengths' },
      { quote: 'Try naming the pause out loud: "I need an hour." It removes the abandonment reading.', source: 'Suggested adjustment' },
    ],
    onboardingPath: '/tps-onboarding?intent=relationships',

    crossSell: { slug: 'career', label: 'Career finder', reason: 'The same traits shape which work environments wear you down.', path: '/career' },
    seo: {
      title: 'Relationship Compatibility Report & Test | Psyforge',
      description: 'Both take a 15-minute assessment and get one honest compatibility report: where you fit, where the friction comes from, and what to do about it.',
    },
    accent: 'primary',
  },

  enterprise: {
    slug: 'enterprise',
    path: '/enterprise',
    label: 'Business Reports',
    audience: 'managers and founders working with a team',
    headline: 'See how your team actually works together',
    headlineAccent: 'actually works together',
    promise:
      'Every person takes one assessment. You get a group report: the composition of the team, where it is thin, and where the predictable friction sits.',
    timeCost: '15 minutes per person, group report on completion',
    outputs: [
      { title: 'A team composition map', body: 'The mix of styles you actually hired, laid out so you can see the gaps rather than guess at them.' },
      { title: 'Pairwise friction and fit', body: 'Every working pair scored, so you know which collaborations need structure and which run themselves.' },
      { title: 'Group blind spots', body: 'Traits your team is short on — the risks nobody in the room is naturally checking for.' },
      { title: 'A report you can hand to leadership', body: 'Exportable group findings written for decisions, not for a psychology seminar.' },
    ],
    steps: [
      { title: 'Invite your team', body: 'Each person takes the same 15-minute assessment on their own time.', time: '15 min each' },
      { title: 'Review the group report', body: 'Composition, gaps and pair dynamics for the whole group in one view.' },
      { title: 'Act on it', body: 'Use it for team pairing, hiring priorities and onboarding conversations.' },
    ],
    faqs: [
      { q: 'What do team members see about each other?', a: 'Individual results belong to the individual. The group view reports composition and pair dynamics, not private detail about any one person.' },
      { q: 'How many people does it take to be useful?', a: 'Meaningful group patterns start at about five participants; pair dynamics work from two.' },
      { q: 'Is this a hiring test?', a: 'No. It is not validated for selection and should not decide who gets hired. It is for understanding and working with the team you have.' },
    ],
    audienceFit: {
      for: [
        'You lead a team of roughly five to fifty and want its shape on one page.',
        'You are deciding who pairs with whom on the next piece of work.',
        'You want to know which risks nobody on the team naturally checks for.',
      ],
      notFor: [
        'You want a hiring or selection test — this is not validated for that.',
        'You want individual profiles of your reports. Those belong to them.',
      ],
    },
    builtOn: {
      items: ['DiSC composition', 'Big Five spread', 'MBTI function coverage', 'Pairwise fit scoring', 'Group blind-spot analysis'],
      note: 'Group findings aggregate individual profiles; private detail never surfaces in the team view.',
    },
    voices: [
      { quote: 'Four drivers, no stabilisers. Decisions land fast and get revisited twice.', source: 'Composition map' },
      { quote: 'Design and engineering score 54% — this pair needs written handoffs, not more meetings.', source: 'Pairwise fit' },
      { quote: 'Nobody here naturally slows a decision down. Add that check deliberately.', source: 'Group blind spot' },
    ],
    onboardingPath: '/tps-onboarding?intent=enterprise',

    crossSell: { slug: 'career', label: 'Career finder', reason: 'Useful for the development conversations that follow a group report.', path: '/career' },
    seo: {
      title: 'Team & Group Personality Reports for Business | Psyforge',
      description: 'Group personality analytics for teams: composition mapping, pairwise working fit, group blind spots, and an exportable report for leadership.',
    },
    accent: 'secondary',
  },

  liker: {
    slug: 'liker',
    path: '/liker',
    label: 'Liker',
    audience: 'people who want better recommendations',
    headline: 'Recommendations from who you are, not what trended',
    headlineAccent: 'who you are',
    promise:
      'Swipe through films, shows, books and music. Liker learns your taste, tells you why each pick fits, and reads your personality back to you from it.',
    timeCost: '2 minutes of swiping to start',
    outputs: [
      { title: 'Picks with a reason attached', body: 'Every recommendation says which part of your taste or personality it is answering.' },
      { title: 'Three kinds of suggestion', body: 'More like this, adjacent taste, and a deliberate stretch — so you are not stuck in one loop.' },
      { title: 'Collections you keep', body: 'Save what lands into lists you can come back to and share.' },
      { title: 'Your taste, read back to you', body: 'What your choices say about how you think and what you are drawn to.' },
    ],
    steps: [
      { title: 'Swipe a few titles', body: 'Like, pass, or mark interested. That is the whole input to start.', time: '2 min' },
      { title: 'Get picks with reasons', body: 'Recommendations arrive with the why, and get sharper as you keep going.' },
      { title: 'See the pattern', body: 'Liker turns your choices into a read on your taste and personality.' },
    ],
    faqs: [
      { q: 'Do I have to take a personality test first?', a: 'No. Start swiping — Liker infers from your choices. Adding an assessment makes the picks noticeably sharper.' },
      { q: 'Where does it recommend from?', a: 'Films, series, books and music, with availability checked so you can actually watch or read what it suggests.' },
      { q: 'Can I see why something was suggested?', a: 'Yes, on every card. That is the point — no unexplained feed.' },
    ],
    audienceFit: {
      for: [
        'You spend longer choosing something to watch than watching it.',
        'You want the reason a pick was made, not an unexplained feed.',
        'You like being pushed slightly outside your usual taste.',
      ],
      notFor: [
        'You want a streaming catalogue to browse by genre.',
        'You want only safe picks — a deliberate stretch is part of every set.',
      ],
    },
    builtOn: {
      items: ['Swipe signal weighting', 'Taste embeddings', 'Personality trait overlays', 'Availability checks'],
      note: 'Your choices carry more weight than any single assessment result.',
    },
    voices: [
      { quote: 'You keep choosing stories that solve a problem through understanding rather than force.', source: 'Why this pick' },
      { quote: 'Adjacent taste: same patience, different genre. Worth one evening.', source: 'Suggestion type' },
      { quote: 'Your passes cluster on spectacle without stakes. That is a preference, not a gap.', source: 'Taste read-back' },
    ],
    onboardingPath: '/liker',

    crossSell: { slug: 'personality', label: 'Your personality profile', reason: 'Your swipes already hint at it — measure it properly.', path: '/tps-onboarding' },
    seo: {
      title: 'Personality-Based Movie, Book & Music Recommendations | Psyforge',
      description: 'Swipe films, shows, books and music and get recommendations with the reason attached — matched to your taste and personality, not to what is trending.',
    },
    accent: 'success',
  },
};

export const TOOL_LANDING_LIST: ToolLanding[] = [
  TOOL_LANDINGS.career,
  TOOL_LANDINGS.relationships,
  TOOL_LANDINGS.astrology,
  TOOL_LANDINGS.enterprise,
  TOOL_LANDINGS.liker,
];

export function getToolLanding(slug: string | null | undefined): ToolLanding | null {
  if (!slug) return null;
  return TOOL_LANDINGS[slug as ToolSlug] ?? null;
}
